import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel, SelectSeparator } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { AlertTriangle } from 'lucide-react';

interface CancelLeadDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (reason: string, notes?: string) => void;
  isLoading?: boolean;
}

const CANCELLATION_REASON_GROUPS = [
  {
    group: '🚫 Not a Lead',
    reasons: [
      { value: 'sales_call', label: 'Sales / vendor call' },
      { value: 'wrong_number', label: 'Wrong number / No answer' },
      { value: 'spam', label: 'Spam' },
      { value: 'existing_customer_diff_number', label: 'Existing customer (Different Number)' },
      { value: 'looking_for_shop', label: 'Looking for shop' },
      { value: 'looking_for_parts', label: 'Looking for parts only' },
    ],
  },
  {
    group: '🛑 Customer Decision',
    reasons: [
      { value: 'not_ready', label: 'Customer not ready' },
      { value: 'price_too_high', label: 'Price too high' },
      { value: 'already_serviced', label: 'Already serviced elsewhere' },
      { value: 'resolved_self', label: 'Issue resolved itself' },
    ],
  },
  {
    group: '⚙️ Operational',
    reasons: [
      { value: 'out_of_area', label: 'Out of service area' },
      { value: 'no_tech_available', label: 'No tech available' },
      { value: 'out_of_scope', label: 'Out of scope (roller shutters, glass, etc.)' },
      { value: 'commercial_door', label: 'Commercial door' },
      { value: 'gate_repair', label: 'Gate repair' },
    ],
  },
  {
    group: '📝 Other',
    reasons: [
      { value: 'other', label: 'Other (please specify)' },
    ],
  },
];

// Flatten for lookup
const ALL_REASONS = CANCELLATION_REASON_GROUPS.flatMap(g => g.reasons);

export function CancelLeadDialog({ open, onOpenChange, onConfirm, isLoading }: CancelLeadDialogProps) {
  const [selectedReason, setSelectedReason] = useState<string>('');
  const [additionalNotes, setAdditionalNotes] = useState('');

  const handleConfirm = () => {
    if (!selectedReason) return;

    const reasonLabel = ALL_REASONS.find(r => r.value === selectedReason)?.label || selectedReason;
    const finalReason = selectedReason === 'other'
      ? additionalNotes.trim() || 'Other reason not specified'
      : reasonLabel;

    onConfirm(finalReason, additionalNotes.trim() || undefined);
    setSelectedReason('');
    setAdditionalNotes('');
  };

  const handleCancel = () => {
    setSelectedReason('');
    setAdditionalNotes('');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="h-10 w-10 rounded-full bg-destructive/10 flex items-center justify-center">
              <AlertTriangle className="h-5 w-5 text-destructive" />
            </div>
            <div>
              <DialogTitle>Cancel Lead</DialogTitle>
              <DialogDescription className="mt-1">
                This will mark the lead as "Lost" and it will no longer appear in active leads.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="cancel-reason">Reason for cancellation *</Label>
            <Select value={selectedReason} onValueChange={setSelectedReason}>
              <SelectTrigger id="cancel-reason">
                <SelectValue placeholder="Select a reason" />
              </SelectTrigger>
              <SelectContent>
                {CANCELLATION_REASON_GROUPS.map((group, idx) => (
                  <div key={group.group}>
                    {idx > 0 && <SelectSeparator />}
                    <SelectGroup>
                      <SelectLabel className="font-semibold text-xs uppercase tracking-wider text-muted-foreground">
                        {group.group}
                      </SelectLabel>
                      {group.reasons.map((reason) => (
                        <SelectItem key={reason.value} value={reason.value}>
                          {reason.label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </div>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Always show notes field — required for "Other", optional otherwise */}
          <div className="space-y-2">
            <Label htmlFor="additional-notes">
              {selectedReason === 'other' ? 'Please specify *' : 'Additional notes (optional)'}
            </Label>
            <Textarea
              id="additional-notes"
              placeholder={selectedReason === 'other'
                ? "Enter the reason for cancellation..."
                : "Any details about why this lead was lost..."
              }
              value={additionalNotes}
              onChange={(e) => setAdditionalNotes(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={!selectedReason || (selectedReason === 'other' && !additionalNotes.trim()) || isLoading}
          >
            {isLoading ? 'Cancelling...' : 'Cancel Lead'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
